import type { FilesScope } from "./source-files"

// The drill column's rules that need no page (drill-column.tsx): its level from the URL, its keys and its focus. Apart,
// importing nothing but a type, so the shell's skeleton reads the level without the page's rules (source-files.ts would
// come into the shell's bundle with it) and the browser tests run the keys as they ship. One column, two levels: the clients, then one scope's reports; each level a listbox with one tab
// stop, focus on the row itself.

// Not client-specific in the URL (?client=none).
export const NOT_CLIENT = "none"

// The column's level is the URL's, so Back and Forward move between the levels. A URL that names a scope or a report
// (?client=, ?set=, ?target=, ?period=, Workflows' &run=) is level 2, that scope's reports: every earlier link opens
// where it did, on its scope's reports (?set= alone under All clients). One that names none (/files, /catalog/workflows)
// is level 1, the clients, with the default pick in the detail. The kind of scope level 2 shows, as the URL says it (a
// client's name is known once the factsheets are); undefined is level 1. Read from the page's params, the router's or a
// URL's.
export function drillScope(query: Partial<Record<"client" | "set" | "target" | "period" | "run", string | string[] | null>>): FilesScope["kind"] | undefined {
  if (query.client) return query.client === NOT_CLIENT ? "none" : "client"
  if (query.set || query.target || query.period || query.run) return "all"
}

// ↑/↓, Home/End over a level's rows; ↑ from the first row leaves the list (the search above level 1, the way back above
// level 2). Enter and Space pick (at level 1 that drills in), → drills in, ←, Backspace and Esc go back.
export function listKeys(input: { ids: () => string[]; focus: (id: string) => void; up?: () => void; pick: (id: string) => void; right?: (id: string) => void; back?: () => void }) {
  return (event: KeyboardEvent) => {
    const id = (event.target as HTMLElement).closest<HTMLElement>('[role="option"]')?.dataset.id
    const ids = input.ids()
    const index = ids.findIndex((item) => item === id)
    if (index < 0 || event.altKey || event.ctrlKey || event.metaKey) return
    const go = (next: number) => input.focus(ids[Math.max(0, Math.min(ids.length - 1, next))])
    const keys: Record<string, (() => void) | undefined> = {
      ArrowDown: () => go(index + 1),
      ArrowUp: () => (index === 0 && input.up ? input.up() : go(index - 1)),
      Home: () => go(0),
      End: () => go(ids.length - 1),
      Enter: () => input.pick(ids[index]),
      " ": () => input.pick(ids[index]),
      ArrowRight: input.right ? () => input.right!(ids[index]) : undefined,
      ArrowLeft: input.back,
      Backspace: input.back,
      Escape: input.back,
    }
    const run = keys[event.key]
    if (!run) return
    event.preventDefault()
    run()
  }
}

// A level's tab stop: the first of `wanted` it lists (the row last focused, the scope it came back from, the pick), else
// its first row.
export const rovingID = (ids: readonly string[], ...wanted: string[]) => wanted.find((id) => id && ids.includes(id)) ?? ids[0] ?? ""

// Whether focus is the column's after one of its focus events, so it follows the next change of level (a drill, the way
// back, the browser's Back or Forward): in on focusin; on focusout out only when it lands elsewhere on the page. A row
// that goes with its level, or a click on nothing, leaves it nowhere, still the column's. Focus the page takes (the
// detail, an update opened in place focusing its Back, a dialog) stays there.
export const focusInside = (event: Pick<FocusEvent, "type" | "relatedTarget">, column: Node) =>
  event.type === "focusin" || !event.relatedTarget || column.contains(event.relatedTarget as Node)

// The list's tab stop, focused.
export function focusList(id: string) {
  const list = document.getElementById(id)
  ;(list?.querySelector<HTMLElement>('[role="option"][tabindex="0"]') ?? list?.querySelector<HTMLElement>('[role="option"]'))?.focus()
}
